import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { Enrolment } from '@jupi/shared'
import { formatDate, formatHours } from '@jupi/ui'

const STATUS_COLOR = { active: 'success', completed: 'primary', withdrawn: 'default' } as const

const day = (iso: string) => formatDate(`${iso}T12:00:00Z`)

// One enrolment at a glance. `showStudent` for an instructor's list, where
// the student is the point; the student's own list shows the course.
export function EnrolmentCard({ enrolment, showStudent = false }: { enrolment: Enrolment; showStudent?: boolean }) {
  const { t } = useTranslation()
  const facts = [
    enrolment.startDate ? t('school.started', { date: day(enrolment.startDate) }) : null,
    enrolment.targetFinishDate ? t('school.targetFinish', { date: day(enrolment.targetFinishDate) }) : null,
    enrolment.creditedHours > 0 ? t('school.credited', { hours: formatHours(enrolment.creditedHours) }) : null,
  ].filter(Boolean)

  return (
    <Card variant="outlined">
      <CardActionArea
        component={RouterLink}
        to={showStudent ? `/school/students/${enrolment.enrolmentId}` : `/school/training/${enrolment.enrolmentId}`}
      >
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>
              {showStudent ? enrolment.studentName : enrolment.courseName}
            </Typography>
            <Chip size="small" color={STATUS_COLOR[enrolment.status]} label={t(`school.status_${enrolment.status}`)} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {showStudent ? enrolment.courseName : enrolment.department.toUpperCase()}
            {enrolment.includesNf ? ' + NF' : ''}
          </Typography>
          {facts.length > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {facts.join(' · ')}
            </Typography>
          ) : null}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}
